import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Avatar, FluentProvider, webLightTheme } from "@fluentui/react-components";
import type { AvatarSize } from "@fluentui/react-components";
import styles from "./Floatingchatbutton.module.css";

/* ------------------------------------------------------------------ *
 *  Floating chat button (Facebook / Messenger "chat head" style).
 *   - Fluent <Avatar>: the logged-in user's photo, or colourful
 *     first + last name initials when there is no photo
 *   - red badge with the number of unread messages + missed calls
 *   - drag it anywhere on the screen (mouse, touch or pen); a plain
 *     click / tap / Enter still calls onClick
 *   - remembers where you left it (localStorage) and stays on screen
 *     when the window is resized
 *   - arrow keys nudge it (Shift = bigger steps) for keyboard users
 * ------------------------------------------------------------------ */

export interface FloatingChatButtonProps {
  /** full name of the logged-in user, e.g. "Jude Iloelunachi" -> initials "JI" */
  name: string;
  /** profile picture URL; falls back to the initials when empty or broken */
  image?: string | null | undefined;
  /** unread messages + missed calls; the red badge is hidden at 0 */
  count?: number | undefined;
  /** called on a click / tap (not after a drag) */
  onClick: () => void;
  /** Fluent Avatar size, also the button diameter in px (default 56) */
  size?: AvatarSize | undefined;
  /** localStorage key used to remember the position */
  storageKey?: string | undefined;
  /** glide to the nearest left / right edge after a drag (default false = stays where dropped) */
  snapToEdge?: boolean | undefined;
  /** hide the button, e.g. while the chat window is open */
  hidden?: boolean | undefined;
  /** accessible label / tooltip */
  label?: string | undefined;
}

interface Pos {
  x: number;
  y: number;
}

const MARGIN = 8;
const DRAG_THRESHOLD = 5; // px of movement before a press counts as a drag

const clampPos = (p: Pos, size: number): Pos => {
  const maxX = Math.max(MARGIN, window.innerWidth - size - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - size - MARGIN);
  return { x: Math.min(Math.max(MARGIN, p.x), maxX), y: Math.min(Math.max(MARGIN, p.y), maxY) };
};

const readPos = (key: string): Pos | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.x === "number" && typeof v.y === "number" ? { x: v.x, y: v.y } : null;
  } catch {
    return null;
  }
};

const writePos = (key: string, p: Pos) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(p));
  } catch {
    /* storage unavailable – the position just won't be remembered */
  }
};

const FloatingChatButton: React.FC<FloatingChatButtonProps> = ({
  name,
  image,
  count = 0,
  onClick,
  size = 36,
  storageKey = "floating-chat-button-pos",
  snapToEdge = false,
  hidden = false,
  label = "Open messages",
}) => {
  const [pos, setPos] = useState<Pos | null>(null);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const posRef = useRef<Pos | null>(null);
  posRef.current = pos;
  const drag = useRef<{ id: number; sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const settleTimer = useRef<number | undefined>(undefined);

  /* first position: where it was left, otherwise bottom-right */
  useLayoutEffect(() => {
    const saved = readPos(storageKey);
    setPos(clampPos(saved || { x: window.innerWidth - size - 24, y: window.innerHeight - size - 24 }, size));
  }, [storageKey, size]);

  /* keep it on screen when the window changes size */
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p, size) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [size]);

  useEffect(() => setImgOk(true), [image]);
  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const commit = useCallback(
    (p: Pos) => {
      setPos(p);
      writePos(storageKey, p);
    },
    [storageKey],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const p = posRef.current;
    if (!p) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not capturable – dragging still works while the pointer stays over the button */
    }
    drag.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, ox: p.x, oy: p.y, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;
      window.clearTimeout(settleTimer.current);
      setSettling(false);
      setDragging(true);
    }
    setPos(clampPos({ x: d.ox + dx, y: d.oy + dy }, size));
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>, cancelled: boolean) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    if (!d.moved) return;
    setDragging(false);
    // the click that follows a drag must not open the chat
    suppressClick.current = true;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 0);
    let p = posRef.current;
    if (!p) return;
    if (snapToEdge && !cancelled) {
      p = { x: p.x + size / 2 < window.innerWidth / 2 ? MARGIN : window.innerWidth - size - MARGIN, y: p.y };
      setSettling(true);
      settleTimer.current = window.setTimeout(() => setSettling(false), 280);
    }
    commit(p);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const step = e.shiftKey ? 40 : 10;
    const p = posRef.current;
    if (!p) return;
    let next: Pos | null = null;
    if (e.key === "ArrowLeft") next = { x: p.x - step, y: p.y };
    else if (e.key === "ArrowRight") next = { x: p.x + step, y: p.y };
    else if (e.key === "ArrowUp") next = { x: p.x, y: p.y - step };
    else if (e.key === "ArrowDown") next = { x: p.x, y: p.y + step };
    if (!next) return;
    e.preventDefault();
    commit(clampPos(next, size));
  };

  if (hidden || !pos) return null;

  const showImage = !!image && imgOk;
  const shown = count > 99 ? "99+" : String(count);

  return (
    // display: contents -> the provider only supplies the Fluent theme, it adds no box of its own
    <FluentProvider theme={webLightTheme} style={{ display: "contents" }}>
      <div
        className={`${styles.root} ${dragging ? styles.dragging : ""} ${settling ? styles.settling : ""}`}
        style={{ width: size, height: size, transform: `translate3d(${pos.x}px, ${pos.y}px, 0)` }}
      >
        <button
          type="button"
          className={styles.fab}
          style={{ width: size, height: size }}
          aria-label={count > 0 ? `${label}, ${count} unread` : label}
          title={`${label} (drag to move)`}
          onClick={() => {
            if (suppressClick.current) return;
            onClick();
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => endDrag(e, false)}
          onPointerCancel={(e) => endDrag(e, true)}
          onKeyDown={onKeyDown}
        >
          {showImage ? (
            <Avatar name={name} size={size} image={{ src: image || "", draggable: false, onError: () => setImgOk(false) }} />
          ) : (
            <Avatar name={name} size={size} color="colorful" />
          )}
        </button>
        {count > 0 && (
          <span key={count} className={styles.badge} aria-hidden="true">
            {shown}
          </span>
        )}
      </div>
    </FluentProvider>
  );
};

export default FloatingChatButton;